import { ReactElement } from 'react';
import { interpolate } from 'remotion';

// Types
import { WavyLineProps } from './types';

// Utils
import { calculatePathLength, generateWavyPath } from './utils';

/**
 * Individual WavyLine component that renders a single animated wavy line
 */
export const WavyLine = ({
  startX,
  startY,
  endX,
  endY,
  wavePhaseOffset,
  entryDelay,
  frame,
  waveConfig,
  lineStyle,
  entryAnimation,
}: WavyLineProps): ReactElement => {
  // Calculate path length for stroke animation
  const pathLength = calculatePathLength(startX, startY, endX, endY);

  // Entry animation: crawl from start to end
  const drawProgress = interpolate(
    frame,
    [entryDelay, entryDelay + entryAnimation.duration],
    [0, 1],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: entryAnimation.easing,
    },
  );

  // Generate the wavy path (static position, wave animation continues)
  const path = generateWavyPath(
    startX,
    startY,
    endX,
    endY,
    frame,
    wavePhaseOffset,
    waveConfig,
  );

  // Calculate stroke-dashoffset for crawling effect
  const strokeDashoffset = pathLength * (1 - drawProgress);

  return (
    <path
      d={path}
      stroke={lineStyle.color}
      strokeWidth={lineStyle.strokeWidth}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={pathLength}
      strokeDashoffset={strokeDashoffset}
      opacity={lineStyle.opacity}
    />
  );
};
